/* eslint-disable react-native/no-inline-styles */
import React, { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { isValidData } from '../commercial/utils';

const PropertyInfoCommercial = forwardRef(({
    pdpData,
    pdpPropertyFees,
    emd,
    status,
    getbidcenterdata,
    isFcOnlineFlag,
}, ref) => {
    const pdpInitData = useSelector(state => state.pdp?.pdpInitData);

    const {
        propertyId,
        detailedAttributes,
        commonAttributes,
        address,
    } = pdpData?.propertyDetails || {};

    const {
        squareFeet,
        numberOfViews,
    } = detailedAttributes || {};

    const commercialFeatures = pdpData?.propertySpecialDetails?.commercialPropertyAdditionalFeatures;

    // Build the list of fields to display as label-value rows
    const fields = [];

    if (isValidData(squareFeet)) {
        fields.push({ label: 'Building Size:', value: squareFeet });
    }
    if (isValidData(commonAttributes?.lotSize)) {
        let unit = (commonAttributes?.lotSizeUnit !== null && commonAttributes?.lotSizeUnit !== '')
            ? commonAttributes.lotSizeUnit
            : 'Sq. Ft.';
        // Capitalize the "a" in acre (e.g. "acre" -> "Acre")
        if (unit && unit.toLowerCase().startsWith('acre')) {
            unit = unit.charAt(0).toUpperCase() + unit.slice(1);
        }
        fields.push({ label: 'Lot Size:', value: `${commonAttributes.lotSize} ${unit}` });
    }
    fields.push({ label: 'Property ID:', value: propertyId });

    if (isValidData(detailedAttributes?.zoning || commonAttributes?.zoning || commercialFeatures?.landZoning || commercialFeatures?.otherZoning)) {
        fields.push({ label: 'Zoning:', value: detailedAttributes?.zoning || commonAttributes?.zoning || commercialFeatures?.landZoning || commercialFeatures?.otherZoning });
    }
    if (isValidData(commonAttributes?.yearBuilt)) {
        fields.push({ label: 'Year Built:', value: commonAttributes.yearBuilt });
    }
    if (isValidData(address?.county)) {
        fields.push({ label: 'County:', value: address.county });
    }
    if (isValidData(pdpInitData?.propertyDetails?.renovatedYear || commercialFeatures?.renovatedYear || commercialFeatures?.otherYearBuiltOrYearRenovated)) {
        fields.push({ label: 'Renovated:', value: pdpInitData?.propertyDetails?.renovatedYear || commercialFeatures?.renovatedYear || commercialFeatures?.otherYearBuiltOrYearRenovated });
    }
    if (isValidData(detailedAttributes?.buildingClass || commonAttributes?.buildingClass || commercialFeatures?.officeClass || commercialFeatures?.otherBuildingClass)) {
        fields.push({ label: 'Building Class:', value: detailedAttributes?.buildingClass || commonAttributes?.buildingClass || commercialFeatures?.officeClass || commercialFeatures?.otherBuildingClass });
    }
    if (isValidData(commonAttributes?.propTypeDisplay) || isValidData(commonAttributes?.type)) {
        const rawType = commonAttributes?.propTypeDisplay || commonAttributes?.type || '';
        // Convert to title case (e.g., "MULTI FAMILY" → "Multi Family")
        const displayType = rawType
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
        fields.push({ label: 'Property Type:', value: displayType });
    }
    if (isValidData(commonAttributes?.subType)) {
        fields.push({ label: 'Sub Type:', value: commonAttributes.subType });
    }
    if (isValidData(pdpInitData?.propertyDetails?.occupancyPercentage || commercialFeatures?.occupancyPercentage)) {
        fields.push({ label: 'Occupancy:', value: (pdpInitData?.propertyDetails?.occupancyPercentage || commercialFeatures?.occupancyPercentage) + '%' });
    }

    // Additional fields from commercialPropertyAdditionalFeatures (with detailedAttributes fallbacks)
    if (isValidData(commercialFeatures?.otherNumberOfStories || detailedAttributes?.floorsNum)) {
        fields.push({ label: 'Stories:', value: commercialFeatures?.otherNumberOfStories || detailedAttributes?.floorsNum });
    }
    if (isValidData(commercialFeatures?.otherNumberOfBuildings)) {
        fields.push({ label: 'Buildings:', value: commercialFeatures.otherNumberOfBuildings });
    }
    if (isValidData(commercialFeatures?.officeNumberOfUnits || commercialFeatures?.retailNumberOfUnits)) {
        fields.push({ label: 'Number of Units:', value: commercialFeatures?.officeNumberOfUnits || commercialFeatures?.retailNumberOfUnits });
    }
    if (isValidData(commercialFeatures?.hospitalityNumberOfRooms || commonAttributes?.bedroomsNum)) {
        fields.push({ label: 'Rooms:', value: commercialFeatures?.hospitalityNumberOfRooms || commonAttributes?.bedroomsNum });
    }
    if (isValidData(commercialFeatures?.otherParcelNumber)) {
        fields.push({ label: 'Parcel Number:', value: commercialFeatures?.otherParcelNumber});
    }
    if (isValidData(commercialFeatures?.otherBuildingFar)) {
        fields.push({ label: 'FAR:', value: commercialFeatures.otherBuildingFar });
    }
    if (isValidData(commercialFeatures?.otherRoofAge)) {
        fields.push({ label: 'Roof Age:', value: `${commercialFeatures.otherRoofAge} years` });
    }
    if (isValidData(detailedAttributes?.mlsNum)) {
        fields.push({ label: 'MLS #:', value: detailedAttributes.mlsNum });
    }

    const viewCount = numberOfViews ? numberOfViews : 0;
    const viewLabel = parseInt(viewCount) === 1 ? 'View' : 'Views';

    return (
        <div ref={ref} className="property-details-card">
            <div className="property-details-card__header">
                <h2 className="property-details-card__title">Property Details</h2>
                <div className="property-details-card__views">
                    <img src="/assets/images/pdp/eye.png" alt="Views" className="property-details-card__views-icon" />
                    <span className="property-details-card__views-count">{viewCount}</span>
                    <span className="property-details-card__views-label">{viewLabel}</span>
                </div>
            </div>
            <div className="property-details-card__body">
                {fields.map((field, index) => (
                    <div key={index} className="property-details-card__row">
                        <span className="property-details-card__label">{field.label}</span>
                        <span className="property-details-card__value">{field.value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
});

PropertyInfoCommercial.displayName = 'PropertyInfoCommercial';

export default PropertyInfoCommercial;
